/**
 * Shared authentication and sync state slice
 * Manages cloud sync status, user authentication, and conflict detection
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SyncStatus } from './SyncTypes';

export interface AuthSyncState {
  /** Timestamp of last successful cloud sync (milliseconds since epoch) */
  lastCloudSyncTimestamp: number;
  
  /** Whether local data has unsaved changes that need to be synced */
  isDirty: boolean;
  
  /** Current sync status: idle, syncing, error, offline */
  syncStatus: SyncStatus;
  
  /** Email of authenticated user (null if not logged in) */
  userEmail: string | null;
  
  /** Cloud timestamp for stale data detection (null if no conflict) */
  cloudTimestamp: number | null;
}

const initialState: AuthSyncState = {
  lastCloudSyncTimestamp: 0,
  isDirty: false,
  syncStatus: SyncStatus.Idle,
  userEmail: null,
  cloudTimestamp: null,
};

const authSyncSlice = createSlice({
  name: 'authSync',
  initialState,
  reducers: {
    setSyncStatus: (state, action: PayloadAction<SyncStatus>) => {
      state.syncStatus = action.payload;
    },
    
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
    
    updateLastCloudSync: (state, action: PayloadAction<number>) => {
      state.lastCloudSyncTimestamp = action.payload;
      state.isDirty = false;
      state.syncStatus = SyncStatus.Idle;
    },
    
    setUserEmail: (state, action: PayloadAction<string | null>) => {
      state.userEmail = action.payload;
    },
    
    setCloudTimestamp: (state, action: PayloadAction<number | null>) => {
      state.cloudTimestamp = action.payload;
    },
  },
});

export const {
  setSyncStatus,
  setIsDirty,
  updateLastCloudSync,
  setUserEmail,
  setCloudTimestamp,
} = authSyncSlice.actions;

export default authSyncSlice.reducer;
